package com.fireavert.administration.yardi_integration_page.yardi_main_page.client.presentation

import YardiSavePropertyDataRequest
import com.fireavert.yardi.common_data.data_models.YardiResidentStatusEnum
import kotlinx.datetime.LocalDate

/**
 * Acts as a simple pass-through to the Interactor for the Admin Test Page.
 * Holds the method signatures expected by the UI component.
 */
class YardiCommonDataController(
    private val interactor: YardiCommonDataInteractor
) {
    suspend fun onLoad() = interactor.onLoad()
    suspend fun savePropertyData(request: YardiSavePropertyDataRequest) = interactor.savePropertyData(request)

    // --- Common Data Test Methods ---
    suspend fun testGetVersionNumber(credentialsId: Int) = interactor.testGetVersionNumber(credentialsId)
    suspend fun testPing(credentialsId: Int) = interactor.testPing(credentialsId)
    suspend fun testGetUnitData(credentialsId: Int, propertyId: String) = interactor.testGetUnitData(credentialsId, propertyId)
    suspend fun testGetResidentData(credentialsId: Int, propertyId: String) = interactor.testGetResidentData(credentialsId, propertyId)
    suspend fun testGetResidentByStatusData(
        credentialsId: Int,
        propertyId: String,
        residentStatus: YardiResidentStatusEnum,
        dateFrom: LocalDate?, // Pass LocalDate
        dateTo: LocalDate?     // Pass LocalDate
    ) = interactor.testGetResidentByStatusData(credentialsId, propertyId, residentStatus, dateFrom, dateTo)
    suspend fun testGetPropertyList(credentialsId: Int, propertyId: String) = interactor.testGetPropertyList(credentialsId, propertyId)
    suspend fun testGetResidentsByUnit(credentialsId: Int, propertyId: String, unitId: String) = interactor.testGetResidentsByUnit(credentialsId, propertyId, unitId)
    suspend fun testGetTenantStatus(credentialsId: Int, propertyId: String) = interactor.testGetTenantStatus(credentialsId, propertyId)
    suspend fun testGetRateLimits(credentialsId: Int) = interactor.testGetRateLimits(credentialsId)
    suspend fun testGetResidentDataResidentialOnly(
        credentialsId: Int,
        propertyId: String,
        tenantCode: String
    ) = interactor.testGetResidentDataResidentialOnly(credentialsId, propertyId, tenantCode)
    suspend fun getPropertyConfigurations(
        credentialsId: Int,
        propertyId: String
    ) = interactor.getPropertyConfigurations(credentialsId, propertyId)
    //suspend fun testGetUnitAttachments(credentialsId: Int) = interactor.testGetUnitAttachments(credentialsId) // Uncomment if needed

    // ================================================================
    // Service Requests Test Methods - UPDATED
    // ================================================================

    suspend fun testGetServiceRequestVersionNumber(credentialsId: Int) =
        interactor.testGetServiceRequestVersionNumber(credentialsId)

    suspend fun testGetServiceRequestPropertyConfigurations(
        credentialsId: Int,
        propertyId: String
    ) = interactor.testGetServiceRequestPropertyConfigurations(credentialsId, propertyId)

    /**
     * Test creating or editing a Yardi Service Request.
     * Matches the parameters collected in the UI.
     */
    suspend fun testCreateEditServiceRequest(
        serviceRequestId: String?, // Nullable string
        credentialId: Int,
        propertyCode: String,
        unitCode: String?, // Nullable strings
        tenantCode: String?,
        vendorCode: String?,
        briefDescription: String?,
        fullDescription: String?,
        accessNotes: String?,
        // --- Added Parameters ---
        origin: String?,
        roomCode: String?,
        priority: String?,
        category: String?,
        subCategory: String?,
        hasPermissionToEnter: Boolean?, // Nullable boolean
        problemDescriptionNotes: String?,
        tenantCaused: Boolean?, // Nullable boolean
        requestorName: String?,
        requestorPhoneNumber: String?,
        requestorEmail: String?
        // dueDate: LocalDate? // Add if needed
    ) = interactor.testCreateEditServiceRequest(
        serviceRequestId = serviceRequestId,// Pass everything down
        credentialsId = credentialId,
        propertyId = propertyCode,
        unitId = unitCode,
        tenantId = tenantCode,
        vendorId = vendorCode,
        description = briefDescription,
        fullDescription = fullDescription,
        accessNotes = accessNotes,
        origin = origin,
        roomCode = roomCode,
        priority = priority,
        category = category,
        subCategory = subCategory,
        hasPermissionToEnter = hasPermissionToEnter,
        problemDescriptionNotes = problemDescriptionNotes,
        tenantCaused = tenantCaused,
        requestorName = requestorName,
        requestorPhoneNumber = requestorPhoneNumber,
        requestorEmail = requestorEmail,
        name = requestorName,
    )

    suspend fun testGetServiceRequests(
        credentialsId: Int,
        propertyId: String,
        serviceId: String? // Nullable to allow getting multiple
    ) = interactor.testGetServiceRequests(credentialsId, propertyId, serviceId!!)
}